import {
	ImageTypeNameEnum,
	ItemTypeEnum,
	ProductComplianceTechnicalHeadingTypeEnum
} from '@inter-ikea-kompis/enums';
import type {
	IDexfSettingsKompisUrls,
	IProduct,
	IProductImage,
	IShoppingItem,
	IShoppingProduct
} from '@inter-ikea-kompis/types';

const ITEM_NO_NUMBER_OF_CHARACTERS = 3;
const ENERGY_CLASS_REGEXP = /^(A\+{0,3}|B|C|D|E)/;

/**
 * A product utility that can be used for formatting article numbers.
 */
export default class ProductUtility {
	/**
	 * Formats a product article number to be more readable for the end user. The string "12535352" will for example be formatted to "125.353.52".
	 *
	 * @param itemNo Product item number.
	 * @returns An item number including dots.
	 */
	public static formatItemNo(itemNo: string): string {
		const numberOfDots = Math.ceil(itemNo.length / ITEM_NO_NUMBER_OF_CHARACTERS);
		const parts = Array(numberOfDots)
			.fill(null)
			.map((_, index) =>
				itemNo.slice(
					index * ITEM_NO_NUMBER_OF_CHARACTERS,
					index * ITEM_NO_NUMBER_OF_CHARACTERS + ITEM_NO_NUMBER_OF_CHARACTERS
				)
			);

		return parts.join('.');
	}

	/**
	 * Returns product information page link.
	 *
	 * @param product Product.
	 * @param localizedInformationOrSettings Either settings retrieved from settings service or localized information fetched from localization service.
	 * @returns Product information page link.
	 */
	public static getProductInformationPageLink(
		product: IProduct,
		localizedInformationOrSettings:
			| { urls: { productInformationPageLinkV2: string } }
			| { kompis: { urls: Pick<IDexfSettingsKompisUrls, 'productInformationPageLinkV2'> } }
	): string | null {
		const productInformationPageLinkV2 =
			'urls' in localizedInformationOrSettings
				? localizedInformationOrSettings.urls.productInformationPageLinkV2
				: localizedInformationOrSettings.kompis.urls.productInformationPageLinkV2;

		if (!product.content || !productInformationPageLinkV2) {
			return null;
		}

		const itemNo = product.content.itemNoLocal || product.content.itemNoGlobal;

		if (!itemNo) {
			return null;
		}

		if (this.isProductSPR(product)) {
			return productInformationPageLinkV2
				.replace('{productItemNo}', itemNo)
				.replace('{lowerCaseSprPrefix}', 's')
				.replace('{upperCaseSprPrefix}', 'S');
		}

		return productInformationPageLinkV2
			.replace('{productItemNo}', itemNo)
			.replace('{lowerCaseSprPrefix}', '')
			.replace('{lowerCaseSprPrefix}', '');
	}

	/**
	 * Returns main image.
	 *
	 * @param product Product.
	 * @returns Product images.
	 */
	public static getMainImages(product: IProduct): IProductImage[] {
		return product.content && product.content.image
			? product.content.image.filter(
					(image) => image.typeName === ImageTypeNameEnum.mainProductPicture
			  )
			: [];
	}

	/**
	 * Checks whether itemType is of type SPR or not.
	 *
	 * @param product IProduct.
	 * @returns Boolean.
	 */
	public static isProductSPR(product: IProduct): boolean {
		return product.content?.itemType === ItemTypeEnum.spr;
	}

	/**
	 * Returns images based on what enum is passed.
	 *
	 * @param product Product - the product to fetch the image from..
	 * @param imageType ImageTypeNameEnum - what type of image to fetch.
	 * @returns Product images.
	 */
	public static getProductImages(product: IProduct, imageType: ImageTypeNameEnum): IProductImage[] {
		const images =
			product.content && product.content.image
				? product.content.image.filter((image) => image.typeName === imageType)
				: [];

		return images;
	}

	/**
	 * Returns energy class.
	 *
	 * @param product Product.
	 * @returns Energy class.
	 */
	public static getEnergyClass(product: IProduct): string | null {
		if (!product.content?.technicalInformation?.length) {
			return null;
		}

		for (const item of product.content.technicalInformation) {
			if (typeof item.valueMetric === 'string') {
				const match = item.valueMetric.match(ENERGY_CLASS_REGEXP);
				if (match) {
					return match[1];
				}
			}
		}

		return null;
	}

	/**
	 * Returns the link to EPREL for the product. EPREL stands for "European Product Registry for Energy Labelling".
	 *
	 * @param product
	 */
	public static getProductInformationSheetLink(product: IProduct): {
		text: string;
		href: string;
	} | null {
		if (product?.content?.complianceTechnical?.length) {
			const productInformationSheetInformation = product.content.complianceTechnical.find(
				(complianceTechnicalItem) =>
					complianceTechnicalItem.headingType === ProductComplianceTechnicalHeadingTypeEnum.rescaled
			);
			if (productInformationSheetInformation) {
				return {
					text: productInformationSheetInformation.headingText,
					href: productInformationSheetInformation.text
				};
			}
		}
		return null;
	}

	/**
	 * Returns energy compliance type (EU/US).
	 *
	 * @param product
	 */
	public static getProductComplianceType(product: IProduct): string | null {
		if (product?.content?.complianceTechnical?.length) {
			const complianceType = product.content.complianceTechnical.find(
				(complianceTechnicalItem) =>
					complianceTechnicalItem.headingType ===
						ProductComplianceTechnicalHeadingTypeEnum.energyEfficiencyClass ||
					complianceTechnicalItem.headingType ===
						ProductComplianceTechnicalHeadingTypeEnum.hyperlink ||
					complianceTechnicalItem.headingType ===
						ProductComplianceTechnicalHeadingTypeEnum.unifiedWaterLabel
			);

			if (complianceType) {
				return complianceType.headingType;
			}
		}

		return null;
	}

	/**
	 * Returns IShoppingItem[] from a IShoppingProduct[].
	 *
	 * @param shoppingProducts
	 */
	public static getShoppingItems(shoppingProducts: IShoppingProduct[]): IShoppingItem[] {
		return shoppingProducts.map((shoppingProduct) => ({
			id: shoppingProduct.product.content?.ruItemNo ?? '',
			type: shoppingProduct.product.content?.ruItemType ?? '',
			quantity: shoppingProduct.quantity
		}));
	}
}
